<template>
	<div class="stretch-layout">
		<wrapper v-if="$slots.buttons" grow>
			<slot name="buttons"/>
		</wrapper>

		<div v-if="$slots.header" class="mt-10 sm:mt-0" :class="{'px-16': fullWidth}">
			<slot name="header"/>
		</div>

		<div class="overflow-x-auto" :class="tableMaxHeightStyle ? 'overflow-y-auto' : 'overflow-y-hidden'" :style="{maxHeight: tableMaxHeightStyle}">
			<table class="table" :class="{'table-fixed-header': fixedHeader, 'table-no-spacing': noSpacing, 'table-dense': dense}">
				<thead>
				<tr class="table-header">
					<table-header-row-data v-for="header in displayHeaders" :header="header" :key="header.value">
						<template v-if="$slots['header.' + header.value]">
							<slot :name="'header.' + header.value" v-bind:header="header"/>
						</template>
					</table-header-row-data>
				</tr>
				</thead>
				<tbody>
				<template v-if="loading">
					<tr v-for="(item, itemKey) in 15" :key="'loader-' + itemKey">
						<td v-for="(header, headerKey) in displayHeaders" :key="'loader-' + itemKey + '-' + headerKey">
							<skeleton width="100%" rounded/>
						</td>
					</tr>
				</template>
				<tr v-if="(!items || !items.length) && !$slots.default">
					<td :colspan="displayHeaders.length">
						<missing-info :text="noDataText ?? $t('missing.data')" class="h-full my-16"/>
					</td>
				</tr>
				<tr v-for="(item, itemIndex) in items" :key="`item-${itemIndex}`" :style="highlightRows[itemIndex] ?? undefined">
					<table-body-row-data v-for="header in displayHeaders" :header="header" :key="`item-${itemIndex}-${header.value}`" :item="item">
						<template v-if="$slots['item.' + header.value]">
							<slot :name="'item.' + header.value" v-bind:item="item" v-bind:index="itemIndex"/>
						</template>

						<template #actions>
							<slot name="actions" v-bind:item="item"/>
						</template>
					</table-body-row-data>
				</tr>

				<slot/>
				</tbody>
			</table>
		</div>
	</div>
</template>

<script>
import TableMixin from "./TableMixin";

export default {
	name: 'SimpleTable',
	mixins: [TableMixin],

	props: {
		// The items to show on the table
		items: {
			type: Array,
			default: []
		},

		// Whether the rows of the table shall receive a smaller height
		dense: {
			type: Boolean,
			default: false
		},

		loading: {
			type: Boolean,
			default: false
		},

		// Whether no spacing shall be added to the bounding cells
		noSpacing: {
			type: Boolean,
			default: false
		},

		// An object with the row index as key and a style object as value
		highlightRows: {
			type: Object,
			default: () => ({})
		}
	},

}
</script>
<style>
/*********************
  Inputs
**********************/
.table {
  position: relative;
  width: 100%;
  border-spacing: 0;
}
.table tr.table-header th {
  border-bottom: 1px solid #e6ecf5;
  color: #5e6b6b;
  opacity: 1;
  padding: 0 12px;
  user-select: none;
  font-size: 12px;
  height: 48px;
  line-height: 1.2;
  z-index: 1;
}
.table tbody tr:not(:last-child) td {
  border-bottom: 1px solid #e6ecf5;
}
.table tbody tr:not(:last-child) td.table-divider {
  border-bottom: 1px solid #d8d8d8;
}
.table tbody tr td {
  color: #465050;
  padding: 6px 12px;
  font-size: 14px;
  height: 48px;
  line-height: 1.3;
}
.table tbody tr td.table-divider {
  border-top: 1px solid #d8d8d8;
  background: #f4f4f5;
  height: 32px;
  line-height: 1.2;
}
.table tbody tr td .table-text-line {
  white-space: nowrap;
}
.table tbody tr.sub-row td {
  height: 42px;
}
.table td.sticky-right, .table th.sticky-right {
  position: sticky;
  right: 0;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
  z-index: 1;
}
.table td.sticky-left, .table th.sticky-left {
  position: sticky;
  left: 0;
  z-index: 1;
}
.table.table-no-spacing tr.table-header th:first-child {
  padding-left: 0;
}
.table.table-no-spacing tr.table-header th:last-child {
  padding-right: 0;
}
.table.table-no-spacing tbody tr td:first-child {
  padding-left: 0;
}
.table.table-no-spacing tbody tr td:last-child {
  padding-right: 0;
}
@media (min-width: 640px) {
  .table.table-full-width td.sticky-right, .table.table-full-width th.sticky-right {
    right: -16px;
  }
  .table.table-full-width td.sticky-left, .table.table-full-width th.sticky-left {
    left: -16px;
  }
}
.table.table-fixed-header thead tr:last-child th {
  top: 0;
  z-index: 2;
  position: sticky;
  background: white;
}
.table.table-dense tr.table-header th {
  height: 42px;
}
.table.table-dense tbody tr td {
  height: 42px;
}
.table.table-dense tbody tr td.table-divider {
  height: 28px;
}
.table.table-dense tbody tr.sub-row td {
  height: 38px;
}
@media (min-width: 0px) and (max-width: 639px) {
  .table.table-dense tr.table-header th {
    height: 38px;
    padding: 0 6px;
  }
  .table.table-dense tbody tr td {
    height: 38px;
    padding: 4px 6px;
    font-size: 13px;
  }
  .table.table-dense tbody tr td.table-divider {
    height: 28px;
  }
  .table.table-dense tbody tr.sub-row td {
    height: 36px;
  }
  .table.table-dense.table-full-width tr.table-header th:first-child {
    padding-left: 12px;
  }
  .table.table-dense.table-full-width tr.table-header th:last-child {
    padding-right: 12px;
  }
  .table.table-dense.table-full-width tbody tr td:first-child {
    padding-left: 12px;
  }
  .table.table-dense.table-full-width tbody tr td:last-child {
    padding-right: 12px;
  }
}
.table.table-mobile-expand tbody tr td .table-text-line {
  white-space: normal;
}
</style>